import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../utils/streamApi';
import { toast } from '../pages/industrial/IndustrialLayout';

// ── Report data: start fresh / delete / restore ───────────────────────────────
// One control for every report built on a ledger (footfall, object counts, vehicles,
// seats, cleaning). Two ways out of bad data — a line drawn wrong all day, an arrow
// flipped at 6pm:
//   Start fresh  the report skips everything before a time. Nothing is deleted;
//                "Show all" undoes it. The default.
//   Delete       rows and their photos leave the report into an archive that can be
//                restored for 7 days.
// Everyone sees the "showing from …" note so a reset report never looks like a quiet
// day; only someone holding the report's SETUP menu (the server decides — can_manage)
// gets the panel.
//
// `items` are the lines / seats / zones THIS report shows. "All" means all of them,
// never the whole camera: a camera can carry a footfall line and a vehicle line, and
// resetting the Footfall Report must not reset the Vehicle Report.

const toTs = (v) => (v ? Math.floor(new Date(v).getTime() / 1000) : null);
const when = (ts) => new Date(ts * 1000).toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
const todayStart = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return Math.floor(d.getTime() / 1000); };

export default function ReportDataMenu({ kind, cam, camName, items = [], onChanged }) {
  const [state, setState] = useState(null);
  const [open, setOpen]   = useState(false);
  const [item, setItem]   = useState('');           // '' = everything on this camera
  const [fromNow, setFromNow] = useState(true);
  const [freshAt, setFreshAt] = useState('');
  const [range, setRange] = useState('today');      // today | before | all | custom
  const [r1, setR1]       = useState('');
  const [r2, setR2]       = useState('');
  const [preview, setPreview] = useState(null);     // dry-run result
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy]   = useState(false);

  const has = cam !== null && cam !== undefined && cam !== '';
  const load = useCallback(() => {
    if (!has) { setState(null); return; }
    apiFetch(`/ui/report-data/state?kind=${kind}&cam=${cam}`).then(r => r.ok ? r.json() : null)
      .then(setState).catch(() => setState(null));
  }, [kind, cam, has]);
  useEffect(() => { load(); setItem(''); setPreview(null); }, [load]);
  useEffect(() => { setPreview(null); setConfirm(''); }, [item, range, r1, r2]);

  const post = async (path, body) => {
    setBusy(true);
    try {
      const r = await apiFetch(path, { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind, cam: Number(cam), items: item ? [item] : (items.length ? items : null), ...body }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Failed');
      return d;
    } catch (e) { toast(e.message || 'Failed', 'err'); return null; }
    finally { setBusy(false); }
  };
  const done = (msg) => { toast(msg, 'ok'); load(); onChanged && onChanged(); };

  const window_ = () => {
    if (range === 'today')  return { ts_from: todayStart(), ts_to: null };
    if (range === 'before') return { ts_from: null, ts_to: toTs(r1) };
    if (range === 'custom') return { ts_from: toTs(r1), ts_to: toTs(r2) };
    return { ts_from: null, ts_to: null };
  };

  const startFresh = async () => {
    const d = await post('/ui/report-data/start-fresh', { ts: fromNow ? null : toTs(freshAt) });
    if (d) done(`Report starts from ${when(d.ts)} — nothing was deleted.`);
  };
  const showAll = async (it) => {
    const d = await post('/ui/report-data/show-all', { items: it === '*' ? null : [it] });
    if (d) done('Showing all data again.');
  };
  const check = async () => {
    const d = await post('/ui/report-data/clear', { ...window_(), dry_run: true });
    if (d) setPreview(d);
  };
  const del = async () => {
    const d = await post('/ui/report-data/clear', window_());
    if (d) { setPreview(null); setConfirm(''); done(`Removed ${d.events} rows and ${d.photos} photos — restorable for 7 days.`); }
  };
  const restore = async (id) => {
    setBusy(true);
    try {
      const r = await apiFetch('/ui/report-data/restore', { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ archive: id }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Restore failed');
      done(`Restored ${d.events} rows.`);
    } catch (e) { toast(e.message, 'err'); }
    finally { setBusy(false); }
  };

  if (!has || !state) return null;
  // Only this report's own lines — the camera's other reports keep their own state.
  const mine = (k) => k === '*' || !items.length || items.includes(k);
  const base = Object.entries(state.baselines || {}).filter(([k]) => mine(k));
  const archives = (state.archives || []).filter(a => !(a.items || []).length || a.items.every(mine));
  const label = (k) => (k === '*' ? `all of ${camName}` : k);

  return (
    <>
      {base.length > 0 && (
        <span style={{ fontSize: 11, color: 'var(--ind-tx3)' }} title="Earlier data is hidden, not deleted.">
          Showing from {base.map(([k, ts]) => `${when(ts)} (${label(k)})`).join(' · ')}
        </span>
      )}
      {state.can_manage && (
        <button className={`ind-pill${open ? ' on' : ''}`} onClick={() => setOpen(o => !o)}>⋯ Report data</button>
      )}

      {open && state.can_manage && (
        <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.45)',
          display: 'grid', placeItems: 'center', padding: 16 }}>
          <div className="ind-card" onClick={e => e.stopPropagation()}
            style={{ width: 'min(560px, 100%)', maxHeight: '90vh', overflowY: 'auto', margin: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <b style={{ fontSize: 14 }}>Report data · {camName}</b>
              <span style={{ flex: 1 }} />
              <button className="ind-pill" onClick={() => setOpen(false)}>✕</button>
            </div>

            <label style={row}>
              <span style={lbl}>Applies to</span>
              <select value={item} onChange={e => setItem(e.target.value)} style={sel}>
                <option value="">{items.length > 1 ? `All ${items.length} on this camera` : (items[0] || 'Everything on this camera')}</option>
                {items.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </label>

            {/* ── Start fresh ── */}
            <div style={box}>
              <span className="ind-eye">Start fresh</span>
              <p style={hint}>The report ignores everything before the chosen time. Nothing is deleted — “Show all” brings it back.</p>
              <label style={row}>
                <input type="radio" checked={fromNow} onChange={() => setFromNow(true)} /> From now
              </label>
              <label style={row}>
                <input type="radio" checked={!fromNow} onChange={() => setFromNow(false)} /> From
                <input type="datetime-local" value={freshAt} onChange={e => { setFreshAt(e.target.value); setFromNow(false); }} style={sel} />
              </label>
              <button className="ind-pill on" disabled={busy || (!fromNow && !freshAt)} onClick={startFresh}>Start fresh</button>
              {base.length > 0 && (
                <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
                  {base.map(([k, ts]) => (
                    <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                      <span style={{ flex: 1 }}>{label(k)} — from {when(ts)}</span>
                      <button className="ind-pill" style={{ padding: '2px 8px' }} disabled={busy}
                        onClick={() => showAll(k)}>Show all</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── Delete ── */}
            <div style={box}>
              <span className="ind-eye">Delete data</span>
              <p style={hint}>Removes the rows and their photos from the report. They can be restored for 7 days, then they are gone.</p>
              <label style={row}>
                <span style={lbl}>Which</span>
                <select value={range} onChange={e => setRange(e.target.value)} style={sel}>
                  <option value="today">Today</option>
                  <option value="before">Everything before a time</option>
                  <option value="custom">Between two times</option>
                  <option value="all">All time</option>
                </select>
              </label>
              {(range === 'before' || range === 'custom') && (
                <label style={row}>
                  <span style={lbl}>{range === 'before' ? 'Before' : 'From'}</span>
                  <input type="datetime-local" value={r1} onChange={e => setR1(e.target.value)} style={sel} />
                  {range === 'custom' && <>to <input type="datetime-local" value={r2} onChange={e => setR2(e.target.value)} style={sel} /></>}
                </label>
              )}
              {!preview && (
                <button className="ind-pill" disabled={busy || (range !== 'today' && range !== 'all' && !r1) || (range === 'custom' && !r2)}
                  onClick={check}>Check what would be removed</button>
              )}
              {preview && (
                preview.events === 0
                  ? <p style={{ ...hint, color: 'var(--ind-tx2)' }}>Nothing in that range.</p>
                  : <div style={{ display: 'grid', gap: 8 }}>
                      <p style={{ ...hint, color: 'var(--ind-tx)' }}>
                        <b>{preview.events}</b> rows and <b>{preview.photos}</b> photos will be removed
                        from {item || (items.length ? items.join(', ') : `all of ${camName}`)}.
                      </p>
                      <label style={row}>
                        <span style={lbl}>Type <b>{camName}</b></span>
                        <input value={confirm} onChange={e => setConfirm(e.target.value)} style={sel} />
                      </label>
                      <button className="ind-pill" disabled={busy || confirm.trim() !== camName}
                        onClick={del} style={{ color: 'var(--ind-bad, #e74c3c)' }}>🗑 Delete</button>
                    </div>
              )}
            </div>

            {/* ── Restore ── */}
            {archives.length > 0 && (
              <div style={box}>
                <span className="ind-eye">Recently deleted</span>
                <div style={{ display: 'grid', gap: 6, marginTop: 6 }}>
                  {archives.map(a => (
                    <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                      <span style={{ flex: 1 }}>
                        {when(a.ts)} · {(a.items || []).join(', ') || 'everything'} · {a.events} rows{a.who ? ` · by ${a.who}` : ''}
                      </span>
                      <button className="ind-pill" style={{ padding: '2px 8px' }} disabled={busy}
                        onClick={() => restore(a.id)}>Restore</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const sel  = { padding: '6px 8px', borderRadius: 6, border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', color: 'var(--ind-tx)', fontSize: 12, maxWidth: '100%' };
const row  = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12, color: 'var(--ind-tx2)', marginBottom: 8 };
const lbl  = { minWidth: 90 };
const hint = { fontSize: 11, color: 'var(--ind-tx3)', margin: '4px 0 10px', lineHeight: 1.5 };
const box  = { border: '1px solid var(--ind-line)', borderRadius: 8, padding: 12, marginTop: 12 };
